import { __esDecorate, __runInitializers } from "tslib";
import { isInteger } from '../primitives/assertion.library.js';
import { Immutable, StringTag } from '../runtime/decorator.library.js';
/**
 * Extracts epoch nanoseconds from a temporal point.
 *
 * @internal
 * @param point - The value containing epoch nanoseconds.
 * @returns The epoch nanoseconds.
 * @throws `TypeError` if the value does not contain an integer epoch nanosecond value.
 */
function getNs(point) {
    const ns = point?.epoch?.ns ?? point?.epochNanoseconds;
    if (isInteger(ns))
        return ns;
    throw new TypeError('Invalid TemporalPoint: missing epoch.ns or epochNanoseconds');
}
/** Represents a continuous span of time with optional start and end boundaries, supporting set operations. */
let Interval = (() => {
    let _classDecorators = [Immutable, StringTag('Interval')];
    let _classDescriptor;
    let _classExtraInitializers = [];
    let _classThis;
    var Interval = class {
        static { _classThis = this; }
        static {
            const _metadata = typeof Symbol === "function" && Symbol.metadata ? Object.create(null) : void 0;
            __esDecorate(null, _classDescriptor = { value: _classThis }, _classDecorators, { kind: "class", name: _classThis.name, metadata: _metadata }, null, _classExtraInitializers);
            Interval = _classThis = _classDescriptor.value;
            if (_metadata) Object.defineProperty(_classThis, Symbol.metadata, { enumerable: true, configurable: true, writable: true, value: _metadata });
            __runInitializers(_classThis, _classExtraInitializers);
        }
        #start;
        #end;
        #startNs;
        #endNs;
        constructor(start, end) {
            const startNs = start === null ? -Infinity : getNs(start);
            const endNs = end === null ? Infinity : getNs(end);
            if (endNs < startNs) {
                this.#start = end;
                this.#end = start;
                this.#startNs = endNs;
                this.#endNs = startNs;
            }
            else {
                this.#start = start;
                this.#end = end;
                this.#startNs = startNs;
                this.#endNs = endNs;
            }
            return Object.freeze(this);
        }
        /** The start point of the interval, or null for unbounded start */
        get start() {
            return this.#start;
        }
        /** The end point of the interval, or null for unbounded end */
        get end() {
            return this.#end;
        }
        /** The start time in nanoseconds since epoch, or -Infinity for unbounded start */
        get startNs() {
            return this.#startNs;
        }
        /** The end time in nanoseconds since epoch, or Infinity for unbounded end */
        get endNs() {
            return this.#endNs;
        }
        /** Returns true if the specified temporal point is within this interval */
        contains(point) {
            const t = getNs(point);
            return t >= this.#startNs && t < this.#endNs;
        }
        /** Returns true if this interval overlaps with another interval */
        overlaps(other) {
            return this.#startNs < other.#endNs && this.#endNs > other.#startNs;
        }
        /** Returns true if this interval starts exactly when the other ends, or vice versa */
        abuts(other) {
            return this.#endNs === other.#startNs || this.#startNs === other.#endNs;
        }
        /** Returns the intersection of this interval and another, or null if they do not overlap */
        intersection(other) {
            if (!this.overlaps(other))
                return null;
            const maxStart = this.#startNs > other.#startNs ? this.#start : other.#start;
            const minEnd = this.#endNs < other.#endNs ? this.#end : other.#end;
            return new Interval(maxStart, minEnd);
        }
        /** Returns the union of this interval and another, or null if they do not overlap/abut */
        union(other) {
            if (!this.overlaps(other) && !this.abuts(other))
                return null;
            const minStart = this.#startNs < other.#startNs ? this.#start : other.#start;
            const maxEnd = this.#endNs > other.#endNs ? this.#end : other.#end;
            return new Interval(minStart, maxEnd);
        }
    };
    return Interval = _classThis;
})();
export { Interval };
